'use client'

import { useState } from 'react'
import { Sparkles, Zap, Check, Brain, ChevronDown } from 'lucide-react'
import { useSkills } from '@/contexts/SkillsContext'
import { cn } from '@/lib/utils'

type SkillSelectorProps = {
  variant?: 'pill' | 'square'
}

export function SkillSelector({ variant = 'pill' }: SkillSelectorProps) {
  const {
    skills,
    loading,
    selectionMode,
    forcedSkillId,
    activeSkill,
    setMode,
    setForcedSkill,
  } = useSkills()

  const [open, setOpen] = useState(false)

  const normalizedMode = selectionMode === 'off' ? 'auto' : selectionMode

  const label =
    normalizedMode === 'auto'
      ? 'Automático'
      : activeSkill?.name || 'Skill'

  const activeColor = activeSkill?.color || '#111827'

  const selectableSkills = skills.filter((skill: any) => {
    const type = String(skill?.type || skill?.agent_type || '').toLowerCase()
    return type !== 'master'
  })

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex items-center justify-center border bg-background text-foreground shadow-sm transition-all hover:border-primary/35 hover:bg-primary/10 hover:text-primary',
          variant === 'square'
            ? 'h-11 w-11 rounded-xl'
            : 'h-7 gap-1.5 rounded-full px-2.5 text-xs font-medium'
        )}
        style={
          activeSkill
            ? {
                borderColor: activeColor,
                color: activeColor,
              }
            : undefined
        }
      >
        {normalizedMode === 'auto' ? (
          <Zap className={cn(variant === 'square' ? 'h-4 w-4' : 'h-3 w-3')} />
        ) : (
          <Sparkles className={cn(variant === 'square' ? 'h-4 w-4' : 'h-3 w-3')} />
        )}

        {variant !== 'square' ? (
          <>
            <span className="max-w-[110px] truncate">{label}</span>
            <ChevronDown className="h-3 w-3 opacity-60" />
          </>
        ) : null}
      </button>

      {open && (
        <div className="fixed inset-0 z-[9998]">
          <button
            type="button"
            className="fixed inset-0 z-[9998] cursor-default"
            onClick={() => setOpen(false)}
            tabIndex={-1}
          />

          <div className="fixed right-6 top-20 z-[9999] w-72 overflow-hidden rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 shadow-2xl">
            <div className="border-b border-neutral-100 dark:border-neutral-800 px-4 py-3">
              <div className="text-sm font-semibold">Modo de respuesta</div>
              <div className="text-xs text-neutral-500">
                Elige automático o fuerza una habilidad especializada.
              </div>
            </div>

            <div className="max-h-96 overflow-y-auto p-2">
              <button
                type="button"
                onClick={() => {
                  setMode('auto')
                  setForcedSkill(null)
                  setOpen(false)
                }}
                className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left hover:bg-neutral-100 dark:hover:bg-neutral-900 transition"
              >
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-neutral-100 dark:bg-neutral-800">
                  <Zap className="h-4 w-4" />
                </div>

                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium">Automático</div>
                  <div className="text-xs text-neutral-500">
                    El agente del sistema decide qué skill usar
                  </div>
                </div>

                {normalizedMode === 'auto' && <Check className="h-4 w-4" />}
              </button>

              <div className="my-2 border-t border-neutral-100 dark:border-neutral-800" />

              <div className="px-3 py-2 text-xs font-semibold text-neutral-500">
                Forzar skill específico
              </div>

              {loading && (
                <div className="px-3 py-3 text-xs text-neutral-500">
                  Cargando skills…
                </div>
              )}

              {!loading && selectableSkills.length === 0 && (
                <div className="px-3 py-3 text-xs text-neutral-500">
                  No tienes skills activos.
                </div>
              )}

              {selectableSkills.map((skill) => {
                const selected = String(forcedSkillId) === String(skill.id)
                const color = skill.color || '#111827'

                return (
                  <button
                    key={String(skill.id)}
                    type="button"
                    onClick={() => {
                      setForcedSkill(skill.id)
                      setOpen(false)
                    }}
                    className="mt-1 flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left hover:bg-neutral-100 dark:hover:bg-neutral-900 transition"
                  >
                    <div
                      className="flex h-9 w-9 items-center justify-center rounded-xl text-white"
                      style={{ background: color }}
                    >
                      <Sparkles className="h-4 w-4" />
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">
                        {skill.name}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-neutral-500">
                        <span>{skill.category || 'Skill'}</span>
                        {skill.status === 'draft' && (
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                            Draft
                          </span>
                        )}
                      </div>
                    </div>

                    {selected && <Check className="h-4 w-4" />}
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export function ActiveSkillBanner() {
  const { activeSkill, setForcedSkill, setMode, selectionMode } = useSkills()

  if (!activeSkill) return null

  const color = activeSkill.color || 'hsl(var(--primary))'

  return (
    <div
      className="flex w-full items-center gap-2 rounded-xl border px-3 py-2 text-xs font-medium shadow-sm"
      style={{
        borderColor: `${color}55`,
        backgroundColor: `${color}12`,
        color,
      }}
    >
      <span
        className="h-2 w-2 shrink-0 rounded-full"
        style={{ backgroundColor: color }}
      />

      <Sparkles className="h-3.5 w-3.5 shrink-0" style={{ color }} />

      <span className="min-w-0 flex-1 truncate">
        Usando:{' '}
        <strong className="font-semibold">
          {activeSkill.name}
        </strong>
        {selectionMode === 'auto' ? (
          <span className="ml-1 opacity-70">
            · automático
          </span>
        ) : null}
      </span>

      <button
        type="button"
        onClick={() => {
          setForcedSkill(null)
          setMode('auto')
        }}
        className="shrink-0 rounded-lg px-2 py-1 text-[11px] font-medium opacity-70 transition hover:bg-background/50 hover:opacity-100"
        style={{ color }}
      >
        cambiar
      </button>
    </div>
  )
}